// Fecha de corte mensual de las actas de pago (payment-monthly-cutoff).
//
// Reglas puras, sin Firebase ni React. Este archivo existe TRES veces, porque
// un paquete de Cloud Functions no puede requerir fuera de su --source:
//
//   src/paymentCutoff.js                                        (frontend, ESM)
//   src/services/backend/gfunctions/handleContract/paymentCutoff.js        (CommonJS)
//   src/services/backend/gfunctions/handlePaymentRequestLegal/paymentCutoff.js (CommonJS)
//
// El cuerpo es idéntico en las tres; solo cambia el bloque final de exports
// (webpack de CRA no acepta imports con nombre desde module.exports). Al
// cambiar una regla, cambiar LAS TRES. `src/paymentCutoff.copies.test.js`
// falla si el cuerpo o la lista de nombres exportados se desincronizan.
//
// Configuración (config/contracts.paymentCutoff):
//
//   {
//     enabled: false,          // interruptor general
//     noticeDays: 5,           // días previos al corte con aviso
//     calendar: {
//       "2026-10": { day: 26, natures: { "Investigativo P405": 30 } },
//       "2026-11": {
//         day: 27, time: "18:00",
//         groups: [{ day: 26, time: "18:00", natures: ["Académico P401", "Administrativo P402"] }],
//         natures: { "Académico P401": { day: 25, time: "12:00" } },
//       },
//       "2026-12": { natures: { "Gobernación de Antioquia": 31 } },
//     },
//   }
//
// Cada clave YYYY-MM guarda SOLO el día (nunca una fecha completa, para que no
// pueda contradecir a la clave). Tres niveles por mes, y para cada naturaleza
// gana el más específico (payment-cutoff-groups-and-time):
//
//   1. `natures`: configuración PROPIA, indexada por la ETIQUETA que guarda
//      `contract.nature` (igual que allowedNatures). Un número es el día con
//      fin del día (la forma anterior a la hora, que el backend viejo lee);
//      un objeto { day, time } lleva hora.
//   2. `groups`: varias naturalezas con un día y una hora. Sin nombre ni id:
//      un grupo es su día + hora dentro del mes, y una naturaleza está como
//      máximo en uno.
//   3. `day` / `time`: la configuración del mes, para todas las naturalezas.
//
// Sin ninguno aplicable, no hay corte. `time` es "HH:MM" (00:01..23:59) y es
// la hora límite SIN incluirla: con "18:00" se puede hasta las 17:59. Sin
// `time` es fin del día (cierra a las 00:00 del día siguiente): así lo
// guardado antes de existir la hora no necesita migración.
//
// Toda la aritmética de fechas es en hora de Colombia con desfase fijo UTC−5
// (sin horario de verano), sin depender de la zona horaria del servidor ni del
// navegador. Los meses de la API son 1..12, como en la clave del calendario.

const BOGOTA_OFFSET_MS = -5 * 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const END_OF_DAY_MINUTES = 24 * 60;

const MONTH_NAMES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

const DEFAULT_PAYMENT_CUTOFF = Object.freeze({
  enabled: false,
  noticeDays: 5,
  calendar: {},
});

const CUTOFF_STATE = Object.freeze({
  OFF: "OFF",         // interruptor apagado o mes sin corte para la naturaleza
  OPEN: "OPEN",       // antes del periodo de aviso
  NOTICE: "NOTICE",   // dentro de los noticeDays previos al corte
  CLOSED: "CLOSED",   // pasado el corte y antes del día 1 del mes siguiente
});

const CUTOFF_ERROR = "CUTOFF_CLOSED";

// --- Utilidades de fecha (Bogotá) --------------------------------------------

const monthKey = (year, month) => `${year}-${String(month).padStart(2, "0")}`;

const daysInMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();

// Año, mes (1..12) y día del calendario de Bogotá para un instante.
const bogotaParts = (date) => {
  const shifted = new Date(date.getTime() + BOGOTA_OFFSET_MS);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
};

// Instante de las 00:00 de Bogotá de esa fecha. Date.UTC normaliza un día o un
// mes fuera de rango (día 32 pasa al mes siguiente, mes 13 al año siguiente).
const bogotaMidnight = (year, month, day) =>
  new Date(Date.UTC(year, month - 1, day) - BOGOTA_OFFSET_MS);

const monthName = (month) => MONTH_NAMES[month - 1] || "";

// "26 de octubre"
const formatDayMonth = (day, month) => `${day} de ${monthName(month)}`;

// "26 de octubre" para un instante, en el calendario de Bogotá.
const formatBogotaDate = (date) => {
  const p = bogotaParts(date);
  return formatDayMonth(p.day, p.month);
};

// "5:59 p. m." para un instante, en hora de Bogotá.
const formatBogotaClock = (date) => {
  const shifted = new Date(date.getTime() + BOGOTA_OFFSET_MS);
  const h = shifted.getUTCHours();
  const m = String(shifted.getUTCMinutes()).padStart(2, "0");
  return `${h % 12 || 12}:${m} ${h < 12 ? "a. m." : "p. m."}`;
};

// "5:59 p. m." para una hora "HH:MM" del calendario, o null.
const formatTime = (time) => {
  const minutes = timeMinutes(time);
  if (minutes === null) return null;
  return formatBogotaClock(new Date(minutes * MINUTE_MS - BOGOTA_OFFSET_MS));
};

// Último minuto permitido con una hora límite: "5:59 p. m." con "18:00" y
// "11:59 p. m." con fin del día (null).
const formatLastMinute = (time) => {
  const minutes = timeMinutes(time);
  const end = minutes === null ? END_OF_DAY_MINUTES : minutes;
  return formatBogotaClock(new Date((end - 1) * MINUTE_MS - BOGOTA_OFFSET_MS));
};

// "26 de noviembre a las 5:59 p. m.": el último minuto en que se puede
// presentar o aprobar con ese corte.
const formatCutoffMoment = (cutoff) =>
  `${formatBogotaDate(cutoff.lastMinute)} a las ${formatBogotaClock(cutoff.lastMinute)}`;

// --- Configuración -----------------------------------------------------------

// Fusiona lo guardado sobre los valores por defecto. Acepta null/undefined
// (documento sin la clave) y basura (devuelve el interruptor apagado).
const resolvePaymentCutoff = (raw) => {
  const src = raw && typeof raw === "object" ? raw : {};
  const noticeDays = Number(src.noticeDays);
  return {
    enabled: src.enabled === true,
    noticeDays: Number.isFinite(noticeDays) && noticeDays >= 0
      ? Math.floor(noticeDays)
      : DEFAULT_PAYMENT_CUTOFF.noticeDays,
    calendar: src.calendar && typeof src.calendar === "object" ? src.calendar : {},
  };
};

const validDay = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.floor(n);
};

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

// Minutos desde las 00:00 de una hora "HH:MM", o null si no es una hora límite
// válida. "00:00" no lo es: cerraría al empezar el día de corte, que es lo
// mismo que el fin del día anterior.
const timeMinutes = (value) => {
  if (typeof value !== "string") return null;
  const m = TIME_PATTERN.exec(value.trim());
  if (!m) return null;
  const minutes = Number(m[1]) * 60 + Number(m[2]);
  return minutes > 0 ? minutes : null;
};

// Hora normalizada "HH:MM" o null (fin del día).
const validTime = (value) => (timeMinutes(value) === null ? null : value.trim());

const monthEntry = (config, year, month) => {
  const calendar = config && config.calendar;
  const entry = calendar && calendar[monthKey(year, month)];
  return entry && typeof entry === "object" ? entry : null;
};

// Configuración propia guardada: un número (día con fin del día) o
// { day, time }. Devuelve { day, time } o null si el día no es válido. Una
// hora inválida se lee como fin del día, para no dejar sin corte a la
// naturaleza.
const readOwn = (value) => {
  if (value && typeof value === "object") {
    const day = validDay(value.day);
    return day === null ? null : { day, time: validTime(value.time) };
  }
  const day = validDay(value);
  return day === null ? null : { day, time: null };
};

// Grupos válidos del mes, con las naturalezas sin repetir. Un grupo sin día
// válido o sin naturalezas se ignora.
const readGroups = (entry) => {
  const raw = entry && Array.isArray(entry.groups) ? entry.groups : [];
  const out = [];
  raw.forEach((g) => {
    if (!g || typeof g !== "object") return;
    const day = validDay(g.day);
    if (day === null) return;
    const natures = Array.from(new Set(
      (Array.isArray(g.natures) ? g.natures : []).filter((n) => typeof n === "string" && n)
    ));
    if (natures.length === 0) return;
    out.push({ day, time: validTime(g.time), natures });
  });
  return out;
};

// Orden de un corte dentro de un mes: día limitado al mes y minuto de cierre.
const settingRank = (setting, year, month) =>
  Math.min(setting.day, daysInMonth(year, month)) * END_OF_DAY_MINUTES
  + (timeMinutes(setting.time) === null ? END_OF_DAY_MINUTES : timeMinutes(setting.time));

const SETTING_SOURCE = Object.freeze({ NATURE: "NATURE", GROUP: "GROUP", MONTH: "MONTH" });

// Configuración que rige a una naturaleza en un mes, o null si no hay corte:
//   { day, time, source }
// Propia > grupo > mes. Un contrato sin naturaleza usa la del mes. Si una
// naturaleza aparece en dos grupos (datos dañados: la pantalla no lo permite)
// gana el corte más temprano, para no dar más plazo del configurado. `day` ya
// está limitado al último día del mes.
const cutoffSettingFor = (config, nature, year, month) => {
  const entry = monthEntry(config, year, month);
  if (!entry) return null;
  const clamp = (s, source) => ({ day: Math.min(s.day, daysInMonth(year, month)), time: s.time, source });
  const hasNature = typeof nature === "string" && nature;

  if (hasNature) {
    const natures = entry.natures && typeof entry.natures === "object" ? entry.natures : {};
    const own = Object.prototype.hasOwnProperty.call(natures, nature) ? readOwn(natures[nature]) : null;
    if (own) return clamp(own, SETTING_SOURCE.NATURE);

    const groups = readGroups(entry)
      .filter((g) => g.natures.includes(nature))
      .sort((a, b) => settingRank(a, year, month) - settingRank(b, year, month));
    if (groups.length > 0) return clamp(groups[0], SETTING_SOURCE.GROUP);
  }

  const day = validDay(entry.day);
  if (day === null) return null;
  return clamp({ day, time: validTime(entry.time) }, SETTING_SOURCE.MONTH);
};

// Día de corte (1..último del mes) de una naturaleza en un mes, o null.
const cutoffDayFor = (config, nature, year, month) => {
  const setting = cutoffSettingFor(config, nature, year, month);
  return setting ? setting.day : null;
};

// Corte de una naturaleza en un mes:
//   day        día de corte
//   time       hora límite "HH:MM", o null (fin del día)
//   source     NATURE | GROUP | MONTH
//   cutoffAt   00:00 Bogotá del día de corte (ancla el inicio del aviso)
//   endsAt     desde aquí la ventana está cerrada: el día de corte a la hora
//              límite, o las 00:00 del día siguiente con fin del día
//   lastMinute un minuto antes de endsAt, para los textos
//   reopensAt  00:00 Bogotá del día 1 del mes siguiente
// Con fin del día el último día del mes, endsAt === reopensAt y la ventana
// cerrada dura cero días.
const cutoffFor = (config, nature, year, month) => {
  const setting = cutoffSettingFor(config, nature, year, month);
  if (!setting) return null;
  const { day, time, source } = setting;
  const cutoffAt = bogotaMidnight(year, month, day);
  const minutes = timeMinutes(time);
  const endsAt = minutes === null
    ? bogotaMidnight(year, month, day + 1)
    : new Date(cutoffAt.getTime() + minutes * MINUTE_MS);
  return {
    year,
    month,
    day,
    time,
    source,
    cutoffAt,
    endsAt,
    lastMinute: new Date(endsAt.getTime() - MINUTE_MS),
    reopensAt: bogotaMidnight(year, month + 1, 1),
  };
};

// Estado de la ventana para una naturaleza en el instante `now`.
//   { state, year, month, cutoff, daysLeft }
// El aviso (NOTICE) empieza a las 00:00 de Bogotá de `noticeDays` días antes
// del día de corte: con corte el 27 y 5 días, desde el 22. `daysLeft` cuenta
// días de calendario hasta el día de corte (0 = el corte es hoy).
const cutoffWindow = (config, nature, now) => {
  const cfg = resolvePaymentCutoff(config);
  const at = now instanceof Date ? now : new Date(now || Date.now());
  const parts = bogotaParts(at);
  if (!cfg.enabled) return { state: CUTOFF_STATE.OFF, year: parts.year, month: parts.month, cutoff: null, daysLeft: null };
  const cutoff = cutoffFor(cfg, nature, parts.year, parts.month);
  if (!cutoff) return { state: CUTOFF_STATE.OFF, year: parts.year, month: parts.month, cutoff: null, daysLeft: null };

  const t = at.getTime();
  const daysLeft = cutoff.day - parts.day;
  if (t >= cutoff.endsAt.getTime() && t < cutoff.reopensAt.getTime()) {
    return { state: CUTOFF_STATE.CLOSED, year: parts.year, month: parts.month, cutoff, daysLeft };
  }
  const noticeFrom = cutoff.cutoffAt.getTime() - cfg.noticeDays * DAY_MS;
  if (t >= noticeFrom) {
    return { state: CUTOFF_STATE.NOTICE, year: parts.year, month: parts.month, cutoff, daysLeft };
  }
  return { state: CUTOFF_STATE.OPEN, year: parts.year, month: parts.month, cutoff, daysLeft };
};

const isAdminActor = (actor) => {
  const roles = actor && Array.isArray(actor.roles) ? actor.roles : [];
  return roles.includes("ADMIN");
};

// Motivo por el que NO se puede presentar ni aprobar un acta o factura ahora,
// o null si se puede. null también con el interruptor apagado y para ADMIN,
// que queda exento para atender excepciones.
const cutoffBlockReason = (config, nature, now, actor) => {
  if (isAdminActor(actor)) return null;
  const w = cutoffWindow(config, nature, now);
  if (w.state !== CUTOFF_STATE.CLOSED) return null;
  const { cutoff } = w;
  return `El plazo para presentar y aprobar los pagos de ${monthName(cutoff.month)} iba hasta el `
    + `${formatCutoffMoment(cutoff)} y ya cerró. `
    + `Se reabre el ${formatBogotaDate(cutoff.reopensAt)}.`;
};

// Aviso para la vista de un contratista o supervisor. Devuelve
// { state, severity, message } o null.
//
// `items` es la lista de contratos a la vista. Cada uno puede ser la etiqueta
// de su naturaleza (string, o null si no tiene) o un objeto { nature, code }.
// Con el código, el aviso puede nombrar los contratos cuando no todos están en
// la misma situación. `options.subject` es "acta" (persona natural, por
// defecto) o "factura" (persona jurídica) y solo cambia el sustantivo.
//
// Reglas:
//   - Gana el corte más próximo que todavía se puede cumplir (NOTICE con el
//     endsAt menor).
//   - Si todos los contratos con corte están cerrados, se avisa el cierre.
//   - Si unos están cerrados y otros no (cortes distintos por naturaleza), el
//     aviso es de advertencia y nombra los cerrados, para que no se lea como
//     si todo estuviera cerrado. Si además hay uno en aviso, se dice su plazo.
//   - Un contrato sin naturaleza usa el día por defecto. Sin contratos, o sin
//     ninguno en aviso o cerrado, no hay aviso.
const MAX_NAMED_CONTRACTS = 3;

// "27 de octubre" con fin del día (cierra a las 00:00) y "26 de noviembre a
// las 6:00 p. m." con hora límite.
const formatClosedSince = (cutoff) => (cutoff.time
  ? `${formatBogotaDate(cutoff.endsAt)} a las ${formatBogotaClock(cutoff.endsAt)}`
  : formatBogotaDate(cutoff.endsAt));

const joinCodes = (codes) => {
  const shown = codes.slice(0, MAX_NAMED_CONTRACTS);
  const rest = codes.length - shown.length;
  if (rest > 0) return `${shown.join(", ")} y ${rest} más`;
  if (shown.length <= 1) return shown.join("");
  return `${shown.slice(0, -1).join(", ")} y ${shown[shown.length - 1]}`;
};

const normalizeNoticeItems = (items) => {
  const list = Array.isArray(items) ? items : [items];
  const seen = new Set();
  const out = [];
  list.forEach((it) => {
    const isObj = it && typeof it === "object";
    const rawNature = isObj ? it.nature : it;
    const nature = typeof rawNature === "string" && rawNature ? rawNature : null;
    const code = isObj && typeof it.code === "string" && it.code ? it.code : null;
    // Sin código se deduplica por naturaleza (mismo resultado para todos).
    const key = code ? `c:${code}` : `n:${nature}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ nature, code });
  });
  return out;
};

const cutoffNotice = (config, items, role, now, options) => {
  const invoice = !!(options && options.subject === "factura");
  const singular = invoice ? "factura" : "acta";
  const plural = invoice ? "facturas" : "actas";
  const isSupervisor = role === "supervisor";
  const action = isSupervisor ? "La aprobación" : "La presentación";

  const entries = normalizeNoticeItems(items).map((it) => ({
    ...it,
    window: cutoffWindow(config, it.nature, now),
  }));
  if (entries.length === 0) return null;

  const closed = entries
    .filter((e) => e.window.state === CUTOFF_STATE.CLOSED)
    .sort((a, b) => a.window.cutoff.endsAt - b.window.cutoff.endsAt);
  const notice = entries
    .filter((e) => e.window.state === CUTOFF_STATE.NOTICE)
    .sort((a, b) => a.window.cutoff.endsAt - b.window.cutoff.endsAt)[0];
  const allClosed = closed.length > 0 && closed.length === entries.length;

  const noticeText = notice
    ? (() => {
      const c = notice.window.cutoff;
      const until = formatCutoffMoment(c);
      return isSupervisor
        ? `Tienes hasta el ${until} para aprobar las ${plural} de este mes.`
        : `Tienes hasta el ${until} para presentar tu ${singular} de este mes.`;
    })()
    : "";

  if (closed.length === 0) {
    if (!notice) return null;
    return { state: CUTOFF_STATE.NOTICE, severity: "info", message: noticeText };
  }

  const first = closed[0].window.cutoff;
  const month = monthName(first.month);
  const reopens = formatBogotaDate(first.reopensAt);

  if (allClosed) {
    return {
      state: CUTOFF_STATE.CLOSED,
      severity: "warning",
      message: `${action} de ${plural} de ${month} está cerrada desde el `
        + `${formatClosedSince(first)}; se reabre el ${reopens}.`,
    };
  }

  // Cortes mezclados: se nombran los contratos cerrados. Sin códigos (p. ej.
  // una vista que solo conoce naturalezas) se nombran las naturalezas.
  const codes = closed.map((e) => e.code).filter(Boolean);
  const natures = Array.from(new Set(closed.map((e) => e.nature || "sin naturaleza")));
  const who = codes.length === closed.length ? joinCodes(codes) : `contratos de ${joinCodes(natures)}`;
  const closedText = `${action} de ${plural} de ${month} ya cerró para ${who}; se reabre el ${reopens}.`;
  return {
    state: CUTOFF_STATE.CLOSED,
    severity: "warning",
    message: notice ? `${noticeText} ${closedText}` : closedText,
  };
};

// --- Detección en el flujo natural (UPDATE_CONTRACT) -------------------------
//
// El acta de persona natural no tiene acción propia: el contratista la envía
// poniendo payments[p].approved = false y el supervisor la aprueba poniéndolo
// en true, y las dos operaciones mandan el mapa `payments` completo. Aquí se
// comparan los dos mapas y se devuelven los periodos con una transición:
//   { key, type: "SUBMIT" | "APPROVE" }
// Reenviar el mismo valor (p. ej. la escritura del PDF después de aprobar, o
// un cliente desactualizado) no es una transición. Tampoco lo es la reversa de
// una aprobación (true → false, permiso REVERT_APPROVAL): es una devolución, y
// la spec deja las devoluciones fuera del bloqueo.
const PAYMENT_TRANSITION = Object.freeze({ SUBMIT: "SUBMIT", APPROVE: "APPROVE" });

const detectPaymentTransitions = (currentPayments, incomingPayments) => {
  const current = currentPayments && typeof currentPayments === "object" ? currentPayments : {};
  const incoming = incomingPayments && typeof incomingPayments === "object" ? incomingPayments : {};
  const transitions = [];
  Object.keys(incoming).forEach((key) => {
    const before = current[key] && typeof current[key] === "object" ? current[key].approved : undefined;
    const after = incoming[key] && typeof incoming[key] === "object" ? incoming[key].approved : undefined;
    if (after === false && before !== false && before !== true) transitions.push({ key, type: PAYMENT_TRANSITION.SUBMIT });
    else if (after === true && before !== true) transitions.push({ key, type: PAYMENT_TRANSITION.APPROVE });
  });
  return transitions;
};

// `UPDATE_CONTRACT` hace `update(data)`, que acepta rutas con puntos: el
// frontend ya escribe `payments.payment_N.activities` o
// `payments.payment_N.chargeAccount` así. Mirar solo `data.payments` dejaba
// pasar `{ "payments.payment_0.approved": true }` sin chequeo de corte. Esto
// arma, desde cualquiera de las tres formas, el mapa { periodo: { approved } }
// que espera detectPaymentTransitions:
//   - `payments`: el mapa completo
//   - `payments.<periodo>`: el periodo completo
//   - `payments.<periodo>.approved`: el campo suelto
// Las demás rutas (`payments.<periodo>.activities`, …) no tocan `approved` y se
// ignoran. Si una misma petición trae varias formas para un periodo, gana la
// más específica, que es la que Firestore aplica al final.
const paymentsPatchFromUpdate = (data) => {
  const src = data && typeof data === "object" ? data : {};
  const out = {};
  if (src.payments && typeof src.payments === "object") {
    Object.keys(src.payments).forEach((key) => { out[key] = src.payments[key]; });
  }
  Object.keys(src).forEach((path) => {
    if (!path.startsWith("payments.")) return;
    const parts = path.split(".");
    const key = parts[1];
    if (!key) return;
    if (parts.length === 2) out[key] = src[path];
  });
  Object.keys(src).forEach((path) => {
    const parts = path.split(".");
    if (parts.length !== 3 || parts[0] !== "payments" || !parts[1] || parts[2] !== "approved") return;
    const prev = out[parts[1]] && typeof out[parts[1]] === "object" ? out[parts[1]] : {};
    out[parts[1]] = { ...prev, approved: src[path] };
  });
  return out;
};

// --- Edición del calendario (pantalla de administración) ---------------------
//
// Devuelven una configuración NUEVA. Todo pasa por `cleanEntry`, que deja el
// mes en su forma canónica: grupos fusionados por día + hora, ordenados por
// día y hora y con sus naturalezas ordenadas; la configuración propia sin hora
// como número (la forma que lee el backend anterior a la hora). Un mes que
// queda sin nada se elimina del mapa. Guardar dos veces lo mismo produce el
// mismo documento.

const byLabel = (a, b) => a.localeCompare(b, "es");

const groupKey = (g) => `${g.day}|${g.time || ""}`;

const groupRank = (g) =>
  g.day * END_OF_DAY_MINUTES + (timeMinutes(g.time) === null ? END_OF_DAY_MINUTES : timeMinutes(g.time));

const ownValue = (day, time) => (time ? { day, time } : day);

// Une los grupos con el mismo día + hora y ordena.
const normalizeGroups = (groups) => {
  const byKey = new Map();
  groups.forEach((g) => {
    const key = groupKey(g);
    const prev = byKey.get(key);
    if (prev) prev.natures = Array.from(new Set([...prev.natures, ...g.natures]));
    else byKey.set(key, { day: g.day, time: g.time || null, natures: g.natures.slice() });
  });
  return Array.from(byKey.values())
    .filter((g) => g.natures.length > 0)
    .sort((a, b) => groupRank(a) - groupRank(b))
    .map((g) => (g.time
      ? { day: g.day, time: g.time, natures: g.natures.slice().sort(byLabel) }
      : { day: g.day, natures: g.natures.slice().sort(byLabel) }));
};

const cleanEntry = (entry) => {
  const next = {};
  const day = validDay(entry && entry.day);
  if (day !== null) {
    next.day = day;
    const time = validTime(entry.time);
    if (time) next.time = time;
  }
  const groups = normalizeGroups(readGroups(entry));
  if (groups.length > 0) next.groups = groups;
  const natures = entry && entry.natures && typeof entry.natures === "object" ? entry.natures : {};
  const kept = {};
  Object.keys(natures).sort(byLabel).forEach((label) => {
    const own = readOwn(natures[label]);
    if (own) kept[label] = ownValue(own.day, own.time);
  });
  if (Object.keys(kept).length > 0) next.natures = kept;
  return Object.keys(next).length > 0 ? next : null;
};

const withEntry = (config, year, month, entry) => {
  const cfg = resolvePaymentCutoff(config);
  const calendar = { ...cfg.calendar };
  const key = monthKey(year, month);
  const cleaned = cleanEntry(entry);
  if (cleaned) calendar[key] = cleaned;
  else delete calendar[key];
  return { ...cfg, calendar };
};

const entryOf = (config, year, month) => monthEntry(resolvePaymentCutoff(config), year, month) || {};

// Hora para escribir: vacía o null es fin del día; cualquier otro valor debe
// ser una hora límite válida.
const writableTime = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") return null;
  const time = validTime(String(value));
  if (!time) throw new Error("La hora debe tener el formato HH:MM, entre 00:01 y 23:59");
  return time;
};

// Acepta { day, time } o solo el día (número o texto).
const settingInput = (value) => (value && typeof value === "object"
  ? { day: value.day, time: value.time }
  : { day: value, time: null });

// Fija (o quita, con día null/"") la configuración del mes. Acepta
// { day, time } o solo el día. Sin día también se quita la hora.
const setMonthDefault = (config, year, month, value) => {
  const input = settingInput(value);
  const day = validDay(input.day);
  const time = day === null ? null : writableTime(input.time);
  const entry = { ...entryOf(config, year, month), day };
  if (time) entry.time = time;
  else delete entry.time;
  return withEntry(config, year, month, entry);
};

// Agrega o edita la configuración propia de una naturaleza ({ day, time } o
// solo el día). Sin `replace`, una naturaleza que ya la tiene en el mes se
// rechaza con un Error.
const setNatureOwn = (config, year, month, nature, value, options) => {
  const label = typeof nature === "string" ? nature.trim() : "";
  if (!label) throw new Error("La naturaleza es obligatoria");
  const input = settingInput(value);
  const day = validDay(input.day);
  if (day === null) throw new Error("El día debe ser un número entre 1 y 31");
  const time = writableTime(input.time);
  const entry = entryOf(config, year, month);
  const natures = entry.natures && typeof entry.natures === "object" ? entry.natures : {};
  const replace = !!(options && options.replace);
  if (!replace && Object.prototype.hasOwnProperty.call(natures, label)) {
    throw new Error(`"${label}" ya tiene configuración propia en ${monthName(month)}: edita la existente`);
  }
  return withEntry(config, year, month, { ...entry, natures: { ...natures, [label]: ownValue(day, time) } });
};

const removeNatureOwnMany = (config, year, month, natureList) => {
  const entry = entryOf(config, year, month);
  const natures = { ...(entry.natures && typeof entry.natures === "object" ? entry.natures : {}) };
  (Array.isArray(natureList) ? natureList : []).forEach((n) => { delete natures[n]; });
  return withEntry(config, year, month, { ...entry, natures });
};

const removeNatureOwn = (config, year, month, nature) =>
  removeNatureOwnMany(config, year, month, [nature]);

// Valida y normaliza un grupo de entrada { day, time, natures }.
const groupInput = (group) => {
  const src = group && typeof group === "object" ? group : {};
  const day = validDay(src.day);
  if (day === null || day > 31) throw new Error("El día debe ser un número entre 1 y 31");
  const time = writableTime(src.time);
  const natures = Array.from(new Set(
    (Array.isArray(src.natures) ? src.natures : [])
      .map((n) => (typeof n === "string" ? n.trim() : ""))
      .filter(Boolean)
  ));
  if (natures.length === 0) throw new Error("Selecciona al menos una naturaleza");
  return { day, time, natures };
};

const editingKey = (editing) => (editing
  ? groupKey({ day: validDay(editing.day), time: validTime(editing.time) })
  : null);

const uniqueMonths = (months) => Array.from(new Set(
  (Array.isArray(months) ? months : [months])
    .map(Number)
    .filter((m) => Number.isInteger(m) && m >= 1 && m <= 12)
)).sort((a, b) => a - b);

// Vista previa de guardar un grupo en uno o varios meses. No modifica nada.
// Por mes:
//   month, day (el escrito), clampedDay (solo si el mes es más corto)
//   moves         [{ nature, from: { day, time } }]  salen de otro grupo
//   ownOverrides  [{ nature, day, time }]  su configuración propia sigue mandando
//   mergeWith     { day, time, natures } | null  grupo existente con el mismo día + hora
// `options.editing` ({ day, time }) es el grupo original cuando se edita, que
// no cuenta como origen de movimientos ni como fusión.
const planGroup = (config, year, months, group, options) => {
  const g = groupInput(group);
  const editing = editingKey(options && options.editing);
  const key = groupKey(g);
  return uniqueMonths(months).map((month) => {
    const entry = entryOf(config, year, month);
    const groups = normalizeGroups(readGroups(entry)).filter((x) => groupKey(x) !== editing);
    const natures = entry.natures && typeof entry.natures === "object" ? entry.natures : {};
    const moves = [];
    g.natures.forEach((nature) => {
      groups.forEach((x) => {
        if (groupKey(x) !== key && x.natures.includes(nature)) {
          moves.push({ nature, from: { day: x.day, time: x.time || null } });
        }
      });
    });
    const ownOverrides = g.natures
      .map((nature) => {
        const own = Object.prototype.hasOwnProperty.call(natures, nature) ? readOwn(natures[nature]) : null;
        return own ? { nature, day: own.day, time: own.time } : null;
      })
      .filter(Boolean);
    const same = groups.find((x) => groupKey(x) === key);
    const last = daysInMonth(year, month);
    return {
      month,
      day: g.day,
      clampedDay: g.day > last ? last : null,
      moves,
      ownOverrides,
      mergeWith: same ? { day: same.day, time: same.time || null, natures: same.natures } : null,
    };
  });
};

// Guarda un grupo en uno o varios meses (copias independientes). Por mes:
// quita el grupo original si se edita, saca las naturalezas elegidas de
// cualquier otro grupo, une con el grupo del mismo día + hora si existe,
// borra las configuraciones propias pedidas y elimina los grupos vacíos.
// `options.removeOwnFor`: arreglo de naturalezas (todos los meses) u objeto
// { mes: [naturalezas] }.
const applyGroup = (config, year, months, group, options) => {
  const g = groupInput(group);
  const opts = options || {};
  const editing = editingKey(opts.editing);
  const removeOwnFor = opts.removeOwnFor || [];
  let next = resolvePaymentCutoff(config);
  uniqueMonths(months).forEach((month) => {
    const entry = entryOf(next, year, month);
    const groups = normalizeGroups(readGroups(entry))
      .filter((x) => groupKey(x) !== editing)
      .map((x) => ({ day: x.day, time: x.time || null, natures: x.natures.filter((n) => !g.natures.includes(n)) }));
    groups.push({ day: g.day, time: g.time, natures: g.natures });
    const natures = { ...(entry.natures && typeof entry.natures === "object" ? entry.natures : {}) };
    const drop = Array.isArray(removeOwnFor) ? removeOwnFor : (removeOwnFor[month] || []);
    drop.forEach((n) => { delete natures[n]; });
    next = withEntry(next, year, month, { ...entry, groups, natures });
  });
  return next;
};

// Elimina el grupo de un mes identificado por su día + hora.
const removeGroup = (config, year, month, group) => {
  const key = editingKey(group);
  const entry = entryOf(config, year, month);
  const groups = normalizeGroups(readGroups(entry)).filter((x) => groupKey(x) !== key);
  return withEntry(config, year, month, { ...entry, groups });
};

// Vista de un mes para la pantalla, ya normalizada:
//   { day, time, groups: [{ day, time, natures }], own: [{ nature, day, time }] }
const monthView = (config, year, month) => {
  const entry = cleanEntry(entryOf(config, year, month)) || {};
  const natures = entry.natures || {};
  return {
    day: entry.day === undefined ? null : entry.day,
    time: entry.time || null,
    groups: (entry.groups || []).map((g) => ({ day: g.day, time: g.time || null, natures: g.natures })),
    own: Object.keys(natures).map((label) => ({ nature: label, ...readOwn(natures[label]) })),
  };
};

// --- exports ---------------------------------------------------------------
export {
  BOGOTA_OFFSET_MS,
  CUTOFF_ERROR,
  CUTOFF_STATE,
  DEFAULT_PAYMENT_CUTOFF,
  MONTH_NAMES,
  PAYMENT_TRANSITION,
  SETTING_SOURCE,
  applyGroup,
  bogotaParts,
  cutoffBlockReason,
  cutoffDayFor,
  cutoffFor,
  cutoffNotice,
  cutoffSettingFor,
  cutoffWindow,
  daysInMonth,
  detectPaymentTransitions,
  formatDayMonth,
  formatLastMinute,
  formatTime,
  monthKey,
  monthName,
  monthView,
  paymentsPatchFromUpdate,
  planGroup,
  removeGroup,
  removeNatureOwn,
  removeNatureOwnMany,
  resolvePaymentCutoff,
  setMonthDefault,
  setNatureOwn,
  validTime,
};
